import { z } from "zod";
import { escapeCsvField, parseCsv } from "./csv-parse.js";
import { apiBigIntIdSchema } from "./ids.js";
import { toCsvSafeField } from "./spreadsheet-text.js";
/** One private CSV per completed import. Not one database row per source line. */
export const CONTACT_IMPORT_REPORT_COLUMNS = [
    "row_number",
    "first_name",
    "last_name",
    "display_name",
    "phone",
    "email",
    "status",
    "reason",
    "birthday_source",
    "birthday_imported",
    "contact_id",
];
export const CONTACT_IMPORT_REPORT_STATUSES = [
    "IMPORTED",
    "RESTORED",
    "SKIPPED",
    "FAILED",
    "IMPORTED_WITH_WARNING",
    "RESTORED_WITH_WARNING",
];
export const CONTACT_IMPORT_REPORT_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_CONTACT_IMPORT_REPORT_PAGE_SIZE = 25;
/**
 * Source-row numbers (`index + 2`) at the end of each write batch.
 * Used as deterministic R2 part keys so a resumed job can still assemble one report.
 */
export function contactImportReportPartSourceRows(totalRows, batchSize) {
    if (totalRows <= 0 || batchSize <= 0)
        return [];
    const ends = [];
    let processed = 0;
    while (processed < totalRows) {
        const count = Math.min(batchSize, totalRows - processed);
        processed += count;
        ends.push(processed + 1);
    }
    return ends;
}
const SAFE_REASON = /^[a-z0-9_]{1,64}$/;
export function safeContactImportReportReason(code, fallback) {
    if (code && SAFE_REASON.test(code))
        return code;
    return fallback;
}
/**
 * Map a classified/written row onto the report status.
 * `failed_create` is the only FAILED outcome, matching `ContactImportJob.failedCount`.
 * A birthday warning never turns a written contact into SKIPPED or FAILED.
 */
export function resolveContactImportReportStatus(input) {
    const birthdaySkipped = input.warningCodes?.includes("birthday_skipped") === true;
    if (input.written === "created") {
        return birthdaySkipped
            ? { status: "IMPORTED_WITH_WARNING", reason: "birthday_skipped" }
            : { status: "IMPORTED", reason: "" };
    }
    if (input.written === "restored") {
        return birthdaySkipped
            ? { status: "RESTORED_WITH_WARNING", reason: "birthday_skipped" }
            : { status: "RESTORED", reason: "" };
    }
    if (input.errorCode === "failed_create") {
        return { status: "FAILED", reason: "failed_create" };
    }
    return {
        status: "SKIPPED",
        reason: safeContactImportReportReason(input.errorCode, "skipped"),
    };
}
function reportCells(row) {
    return [
        String(row.rowNumber),
        row.firstName ?? "",
        row.lastName ?? "",
        row.displayName ?? "",
        row.phone ?? "",
        row.email ?? "",
        row.status,
        safeContactImportReportReason(row.reason, row.reason === "" ? "" : "skipped"),
        row.birthdaySource ?? "",
        row.birthdayImported ?? "",
        row.contactId == null ? "" : String(row.contactId),
    ];
}
function encodeReportLine(cells, guardSpreadsheet) {
    return cells
        .map((cell) => escapeCsvField(guardSpreadsheet ? toCsvSafeField(cell) : cell))
        .join(",");
}
/** Header plus one line per source row. Stored once; downloads may localize `reason`. */
export function buildContactImportReportCsv(rows) {
    const lines = [
        CONTACT_IMPORT_REPORT_COLUMNS.join(","),
        ...rows.map((row) => encodeReportLine(reportCells(row), true)),
    ];
    return `\uFEFF${lines.join("\n")}\n`;
}
/** Batch fragment without a header, appended later in source order. */
export function buildContactImportReportCsvBody(rows) {
    if (rows.length === 0)
        return "";
    return `${rows.map((row) => encodeReportLine(reportCells(row), true)).join("\n")}\n`;
}
const REASON_LABELS = {
    en: {
        duplicate_phone: "This phone already belongs to an active contact",
        duplicate_email: "This email already belongs to an active contact",
        invalid_phone: "Invalid phone number",
        invalid_email: "Invalid email address",
        missing_country: "Country is required for local phone numbers",
        no_identity: "Row has no name, phone, or email",
        ambiguous_deleted_phone: "More than one deleted contact uses this phone number",
        ambiguous_match: "Multiple matching WhatsApp profiles",
        failed_create: "The contact could not be saved",
        birthday_skipped: "Birthday was skipped because it was not a valid date",
        skipped: "Row was skipped",
    },
    he: {
        duplicate_phone: "המספר כבר שייך לאיש קשר פעיל",
        duplicate_email: "האימייל כבר שייך לאיש קשר פעיל",
        invalid_phone: "מספר טלפון לא תקין",
        invalid_email: "כתובת אימייל לא תקינה",
        missing_country: "נדרשת מדינה למספר מקומי",
        no_identity: "בשורה אין שם, טלפון או אימייל",
        ambiguous_deleted_phone: "יותר מאיש קשר מחוק אחד משתמש במספר זה",
        ambiguous_match: "נמצאו כמה פרופילי וואטסאפ תואמים",
        failed_create: "לא ניתן היה לשמור את איש הקשר",
        birthday_skipped: "תאריך הלידה דולג כי אינו תאריך תקין",
        skipped: "השורה דולגה",
    },
};
/** Stable codes stay in R2. Download replaces `reason` with a label. Unknown codes stay codes. */
export function localizeContactImportReportReason(code, locale) {
    if (code.length === 0)
        return "";
    return REASON_LABELS[locale][code] ?? code;
}
export function localizeContactImportReportCsv(csv, locale) {
    const rows = parseCsv(csv.replace(/^\uFEFF/, ""), { skipBlankRows: false });
    if (rows.length === 0)
        return csv;
    const reasonIndex = rows[0].indexOf("reason");
    if (reasonIndex < 0)
        return csv;
    const lines = rows
        .filter((row, index) => index === 0 || row.some((cell) => cell.length > 0))
        .map((row, index) => {
        const cells = index === 0
            ? row
            : row.map((cell, cellIndex) => cellIndex === reasonIndex ? localizeContactImportReportReason(cell, locale) : cell);
        return cells.map((cell) => escapeCsvField(cell)).join(",");
    });
    return `\uFEFF${lines.join("\n")}\n`;
}
export const contactImportReportAvailabilitySchema = z.enum([
    "available",
    "expired",
    "unavailable",
]);
/**
 * `expiresAt` is the 48-hour report window. `storageDeletedAt` means the report
 * object is already gone (cleanup, or it was never stored).
 */
export function contactImportReportAvailability(input) {
    if (input.status !== "COMPLETED")
        return "unavailable";
    const expiresAt = input.expiresAt instanceof Date ? input.expiresAt : new Date(input.expiresAt);
    const now = input.now ?? new Date();
    if (!(expiresAt.getTime() > now.getTime()))
        return "expired";
    if (input.storageDeletedAt != null)
        return "unavailable";
    return "available";
}
export const contactImportReportListItemSchema = z.object({
    id: apiBigIntIdSchema,
    originalFileName: z.string(),
    completedAt: z.string(),
    totalRows: z.number().int().nonnegative(),
    createdCount: z.number().int().nonnegative(),
    restoredCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    warningCount: z.number().int().nonnegative(),
    reportAvailability: contactImportReportAvailabilitySchema,
});
const contactImportReportDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const contactImportReportListQuerySchema = z
    .object({
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce
        .number()
        .int()
        .refine((n) => CONTACT_IMPORT_REPORT_PAGE_SIZES.includes(n), {
        message: "Invalid pageSize",
    })
        .optional(),
    search: z.string().max(200).optional(),
    fromDate: contactImportReportDateSchema.optional(),
    toDate: contactImportReportDateSchema.optional(),
})
    .superRefine((value, ctx) => {
    if ((value.fromDate != null) !== (value.toDate != null)) {
        ctx.addIssue({
            code: "custom",
            message: "`fromDate` and `toDate` must be provided together",
            path: value.fromDate == null ? ["fromDate"] : ["toDate"],
        });
    }
    if (value.fromDate && value.toDate && value.fromDate > value.toDate) {
        ctx.addIssue({
            code: "custom",
            message: "`fromDate` must be on or before `toDate`",
            path: ["fromDate"],
        });
    }
})
    .transform((value) => {
    const search = value.search?.trim();
    return {
        page: value.page,
        pageSize: value.pageSize,
        ...(search ? { search } : {}),
        ...(value.fromDate && value.toDate
            ? { fromDate: value.fromDate, toDate: value.toDate }
            : {}),
    };
});
export const contactImportReportListResponseSchema = z.object({
    items: z.array(contactImportReportListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
export const contactImportReportDownloadQuerySchema = z.object({
    locale: z.enum(["en", "he"]).optional(),
});
//# sourceMappingURL=contact-import-report.js.map